import { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import axiosInstance from "../networking/axiosconfig";
import toast, { Toaster } from "react-hot-toast";

export default function ChatbotSettingPopup({ selectedId, onClose }) {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [formData, setFormData] = useState({
        title: "",
        key: "",
        value: ""
    });

    /* ================= FETCH SETTING ================= */
    useEffect(() => {
        if (!selectedId) return;
        const fetchSetting = async () => {
            try {
                const res = await axiosInstance.get("/api/setting/");

                if (res.data?.status && Array.isArray(res.data.data)) {
                    const setting = res.data.data.find(
                        (item) => item.setting_id_PK === selectedId
                    );

                    if (setting) {
                        setFormData({
                            title: setting.title || "",
                            key: setting.key || "",
                            value: setting.value || ""
                        });
                    } else {
                        toast.error("Setting not found");
                    }
                }
            } catch (err) {
                console.error(err);
                toast.error("Failed to load setting");
            }
        };

        fetchSetting();
    }, [selectedId]);

    /* ================= HANDLE CHANGE ================= */
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError("");
    };

    /* ================= VALIDATION ================= */
    const validate = () => {
        if (!formData.value.trim()) {
            setError("Bot token is required");
            return false;
        }
        return true;
    };

    /* ================= UPDATE ================= */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            await axiosInstance.patch(`/api/setting/${selectedId}`, {
                value: formData.value
            });

            toast.success("Chatbot token updated successfully");

            setTimeout(() => {
                setOpen(false);
                onClose?.();
            }, 1500);
        } catch (err) {
            console.error(err);
            toast.error("Update failed");
        } finally {
            setLoading(false);
        }
    };

    /* ================= UI ================= */
    return (
        <Dialog open={open} onClose={() => { setOpen(false); onClose?.(); }} className="relative z-50">
            <Toaster position="top-right" />

            <DialogBackdrop className="fixed inset-0 bg-black/50" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <DialogPanel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">

                    <div className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white p-4 flex justify-between items-center rounded-xl">
                        <DialogTitle className="mx-auto flex text-xl font-inter font-semibold  items-center justify-center ">
                            Edit Chatbot Token
                        </DialogTitle>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">

                        {/* Title (readonly) */}
                        <div>
                            <label className="text-base font-medium">Title</label>
                            <input
                                value={formData.title}
                                disabled
                                className="w-full mt-1 rounded-md bg-gray-100 px-3 py-2 text-sm"
                            />
                        </div>

                        {/* Key (readonly) */}
                        <div>
                            <label className="text-bade font-medium">Key</label>
                            <input
                                value={formData.key}
                                disabled
                                className="w-full mt-1 rounded-md bg-gray-100 px-3 py-2 text-sm"
                            />
                        </div>

                        {/* Value */}
                        <div>
                            <label className="text-base font-medium">
                                Bot Token <span className="text-red-500">*</span>
                            </label>
                            <input
                                name="value"
                                value={formData.value}
                                onChange={handleChange}
                                placeholder="Enter Telegram Bot Token"
                                className={`w-full mt-1 rounded-md px-3 py-2 text-sm outline outline-1
                ${error ? "outline-red-500" : "outline-gray-300 focus:outline-blue-500"}`}
                            />
                            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => { setOpen(false); onClose?.(); }}
                                className="border border-blue-500 px-4 py-2 rounded-md text-sm"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={loading}
                                className={`bg-blue-600 text-white px-6 py-2 rounded-md text-sm
                ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-500"}`}
                            >
                                {loading ? "Saving..." : "Save"}
                            </button>
                        </div>

                    </form>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
